import { ChevronDown, Copy, Dices, Lock, LockOpen, Moon, Redo2, Save, Sun, TriangleAlert, Type, Undo2 } from 'lucide-react';
import { useState, type CSSProperties, type ReactNode } from 'react';
import {
  POST_COLOR_ROLES,
  POST_COLOR_ROLE_LABELS,
  POST_COLOR_ROLE_USES,
  POST_FONT_STYLES,
  POST_FONT_STYLE_LABELS,
  POST_PALETTE_PRESETS,
  POST_SWATCHES,
  POST_BLACK,
  POST_WHITE,
  contrastRatio,
  postContrastProblems,
  postPaletteSchema,
  type PostColorRole,
  type PostFontStyle,
  type PostPalette,
} from '@nest-recruit/shared';
import { Input } from '../../../../components/Input';
import { MenuItem, Popover, usePopover } from '../../../../components/Popover';
import { useToasts } from '../../../../stores/toasts';
import { EMPTY_HISTORY, SHUFFLE_STYLE_LABELS, flipPalette, paletteText, redo, remember, shufflePalette, undo, type PaletteHistory, type ShuffleStyle } from './palette-tools';
import styles from './ColourToolbar.module.css';

const SHUFFLE_STYLES: readonly ShuffleStyle[] = ['shades', 'pairs', 'contrast'];
const PICKER_WIDTH = 280;
const MENU_WIDTH = 240;
const FONTS_WIDTH = 300;

interface ColourToolbarProps {
  readonly palette: PostPalette;
  readonly headlineFont: PostFontStyle;
  readonly bodyFont: PostFontStyle;
  readonly disabled: boolean;
  readonly canSaveToBrandKit: boolean;
  readonly onPalette: (palette: PostPalette) => void;
  readonly onFonts: (fonts: { readonly headlineFont: PostFontStyle; readonly bodyFont: PostFontStyle }) => void;
  readonly onSaveToBrandKit: () => void;
}

/** Words on a chip read in black or white, whichever shows on its colour. */
function chipInk(colour: string): string {
  return contrastRatio(POST_WHITE, colour) >= contrastRatio(POST_BLACK, colour) ? POST_WHITE : POST_BLACK;
}

function isDark(palette: PostPalette): boolean {
  return contrastRatio(palette.background, POST_BLACK) < contrastRatio(palette.background, POST_WHITE);
}

/**
 * ADR 0066 — the Job post's colour toolbar (the owner's reference, 2026-10-28): five labelled colour chips, each with a
 * lock, then light/dark, shuffle, undo/redo, save to brand kit, copy codes and fonts. Every change shows at once in the
 * live preview and saves with the design; nothing here costs money.
 */
export function ColourToolbar({ palette, headlineFont, bodyFont, disabled, canSaveToBrandKit, onPalette, onFonts, onSaveToBrandKit }: ColourToolbarProps): ReactNode {
  const show = useToasts((toasts) => toasts.show);
  const [locked, setLocked] = useState<ReadonlySet<PostColorRole>>(new Set());
  const [history, setHistory] = useState<PaletteHistory>(EMPTY_HISTORY);
  const [shuffleStyle, setShuffleStyle] = useState<ShuffleStyle>('pairs');
  const { anchor: shuffleAnchor, triggerRef: shuffleRef, isOpen: isShuffleOpen, toggle: toggleShuffle, close: closeShuffle } = usePopover();
  const { anchor: fontsAnchor, triggerRef: fontsRef, isOpen: isFontsOpen, toggle: toggleFonts, close: closeFonts } = usePopover();
  const problems = postContrastProblems(palette);

  const change = (next: PostPalette): void => {
    setHistory((current) => remember(current, palette));
    onPalette(next);
  };
  const toggleLock = (role: PostColorRole): void =>
    setLocked((current) => {
      const next = new Set(current);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  const step = (move: typeof undo): void => {
    const result = move(history, palette);
    if (!result) return;
    setHistory(result.history);
    onPalette(result.palette);
  };
  const copy = (): void => {
    void navigator.clipboard.writeText(paletteText(palette, POST_COLOR_ROLE_LABELS)).then(
      () => show({ tone: 'success', message: 'Colour codes copied' }),
      () => show({ tone: 'danger', message: "Couldn't copy the colour codes" }),
    );
  };

  return (
    <div className={styles.toolbar} role="toolbar" aria-label="Colours and fonts">
      <div className={styles.chips}>
        {POST_COLOR_ROLES.map((role) => (
          <ColourChip
            key={role}
            role={role}
            colour={palette[role]}
            isLocked={locked.has(role)}
            problem={problems.find((item) => item.role === role)?.message ?? null}
            disabled={disabled}
            onChange={(colour) => change({ ...palette, [role]: colour })}
            onToggleLock={() => toggleLock(role)}
          />
        ))}
      </div>
      <div className={styles.tools}>
        <ToolButton label={isDark(palette) ? 'Switch to light' : 'Switch to dark'} disabled={disabled} onClick={() => change(flipPalette(palette))}>
          {isDark(palette) ? <Moon size={16} /> : <Sun size={16} />}
        </ToolButton>
        <div className={styles.split}>
          <ToolButton label={`Shuffle: ${SHUFFLE_STYLE_LABELS[shuffleStyle]}. Locked colours stay.`} disabled={disabled} onClick={() => change(shufflePalette(palette, locked, shuffleStyle, Math.random))}>
            <Dices size={16} />
          </ToolButton>
          <button ref={shuffleRef} type="button" className={styles.splitArrow} aria-label="Shuffle style and ready-made palettes" aria-expanded={isShuffleOpen} disabled={disabled} onClick={toggleShuffle}>
            <ChevronDown size={14} />
          </button>
          <Popover anchor={shuffleAnchor} onClose={closeShuffle} label="Shuffle style" width={MENU_WIDTH}>
            {SHUFFLE_STYLES.map((style) => (
              <MenuItem key={style} isSelected={style === shuffleStyle} onClick={() => { setShuffleStyle(style); closeShuffle(); }}>
                {SHUFFLE_STYLE_LABELS[style]}
              </MenuItem>
            ))}
            <div className={styles.menuDivider} />
            {POST_PALETTE_PRESETS.map((preset) => (
              <MenuItem key={preset.name} icon={<PaletteDots palette={preset.palette} />} onClick={() => { change(preset.palette); closeShuffle(); }}>
                {preset.name}
              </MenuItem>
            ))}
          </Popover>
        </div>
        <ToolButton label="Undo" disabled={disabled || history.past.length === 0} onClick={() => step(undo)}>
          <Undo2 size={16} />
        </ToolButton>
        <ToolButton label="Redo" disabled={disabled || history.future.length === 0} onClick={() => step(redo)}>
          <Redo2 size={16} />
        </ToolButton>
        {canSaveToBrandKit && (
          <ToolButton label="Save colours and fonts to the venture's brand kit" disabled={disabled} onClick={onSaveToBrandKit}>
            <Save size={16} />
          </ToolButton>
        )}
        <ToolButton label="Copy the colour codes" onClick={copy}>
          <Copy size={16} />
        </ToolButton>
        <span className={styles.divider} aria-hidden />
        <button ref={fontsRef} type="button" className={styles.fontsButton} aria-expanded={isFontsOpen} disabled={disabled} onClick={toggleFonts}>
          <Type size={16} aria-hidden />
          Fonts
        </button>
        <Popover anchor={fontsAnchor} onClose={closeFonts} label="Fonts" align="right" width={FONTS_WIDTH}>
          <FontPicker title="Headline" value={headlineFont} onPick={(font) => onFonts({ headlineFont: font, bodyFont })} />
          <FontPicker title="Everything else" value={bodyFont} onPick={(font) => onFonts({ headlineFont, bodyFont: font })} />
          <p className={styles.fontsNote}>The AI copies the look of a font, not the exact font.</p>
        </Popover>
      </div>
    </div>
  );
}

function ToolButton({ label, disabled = false, onClick, children }: { readonly label: string; readonly disabled?: boolean; readonly onClick: () => void; readonly children: ReactNode }): ReactNode {
  return (
    <button type="button" className={styles.tool} aria-label={label} title={label} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}

function ColourChip({
  role,
  colour,
  isLocked,
  problem,
  disabled,
  onChange,
  onToggleLock,
}: {
  readonly role: PostColorRole;
  readonly colour: string;
  readonly isLocked: boolean;
  readonly problem: string | null;
  readonly disabled: boolean;
  readonly onChange: (colour: string) => void;
  readonly onToggleLock: () => void;
}): ReactNode {
  const { anchor, triggerRef, isOpen, toggle, close } = usePopover();
  const [typed, setTyped] = useState(colour);
  const chip = { '--chip-fill': colour, '--chip-ink': chipInk(colour) } as CSSProperties;
  const openPicker = (): void => {
    setTyped(colour);
    toggle();
  };
  const commitTyped = (): void => {
    const parsed = postPaletteSchema.shape.text.safeParse(typed);
    if (parsed.success) onChange(parsed.data);
  };
  return (
    <div className={styles.chip} style={chip}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.chipFace}
        aria-label={`${POST_COLOR_ROLE_LABELS[role]} colour, ${colour}: ${POST_COLOR_ROLE_USES[role]}`}
        title={`${POST_COLOR_ROLE_LABELS[role]}: ${POST_COLOR_ROLE_USES[role]}`}
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={openPicker}
      >
        {POST_COLOR_ROLE_LABELS[role]}
        {problem && <TriangleAlert size={13} className={styles.chipWarning} aria-label={problem} />}
      </button>
      <button type="button" className={styles.chipLock} aria-label={isLocked ? `Unlock ${POST_COLOR_ROLE_LABELS[role]}` : `Lock ${POST_COLOR_ROLE_LABELS[role]} so shuffle keeps it`} aria-pressed={isLocked} disabled={disabled} onClick={onToggleLock}>
        {isLocked ? <Lock size={11} /> : <LockOpen size={11} />}
      </button>
      <Popover anchor={anchor} onClose={close} label={`${POST_COLOR_ROLE_LABELS[role]} colour`} width={PICKER_WIDTH}>
        <div className={styles.picker}>
          <p className={styles.pickerTitle}>
            {POST_COLOR_ROLE_LABELS[role]} <span>· {POST_COLOR_ROLE_USES[role]}</span>
          </p>
          <div className={styles.swatches}>
            {POST_SWATCHES.map((swatch) => (
              <button
                key={swatch}
                type="button"
                className={styles.swatch}
                style={{ '--chip-fill': swatch } as CSSProperties}
                aria-label={swatch}
                aria-pressed={swatch === colour}
                onClick={() => onChange(swatch)}
              />
            ))}
          </div>
          <Input aria-label="Colour code" value={typed} maxLength={7} onChange={(event) => setTyped(event.target.value)} onBlur={commitTyped} onKeyDown={(event) => event.key === 'Enter' && commitTyped()} />
          {problem && <p className={styles.pickerProblem}>{problem}</p>}
        </div>
      </Popover>
    </div>
  );
}

function PaletteDots({ palette }: { readonly palette: PostPalette }): ReactNode {
  return (
    <span className={styles.paletteDots} aria-hidden>
      {POST_COLOR_ROLES.map((role) => (
        <span key={role} style={{ '--chip-fill': palette[role] } as CSSProperties} />
      ))}
    </span>
  );
}

function FontPicker({ title, value, onPick }: { readonly title: string; readonly value: PostFontStyle; readonly onPick: (font: PostFontStyle) => void }): ReactNode {
  return (
    <div className={styles.fontGroup}>
      <p className={styles.fontTitle}>{title}</p>
      {POST_FONT_STYLES.map((font) => (
        <MenuItem key={font} isSelected={font === value} onClick={() => onPick(font)}>
          <span className={styles.fontSample} data-font={font}>
            {POST_FONT_STYLE_LABELS[font]}
          </span>
        </MenuItem>
      ))}
    </div>
  );
}
